// Textos de una fila de la lista de códigos de descuento y cupones.
import { formatDateMx } from '@/lib/dateHelpers';
import type {
    CouponScope,
    CouponStatus,
    DiscountCode,
    DiscountCodeStatus,
    DiscountValue,
} from '../api/promotionsContract';
import { formatInt } from './promotionsFormat';
import { formatCents } from './promotionsRow';

export const CODE_STATUS_LABEL: Record<DiscountCodeStatus | CouponStatus, string> = {
    ACTIVE: 'Vigente',
    USED: 'Usado',
    EXHAUSTED: 'Agotado',
    EXPIRED: 'Vencido',
    VOIDED: 'Anulado',
};

const COUPON_SCOPE_TEXT: Record<CouponScope, string> = {
    CHAIN: 'Toda la cadena',
    ISSUING_STORE: 'Solo la tienda que lo emitió',
    PROMOTION_STORES: 'Tiendas de la promo',
};

const NONE = '—';

export function kindLabel(code: DiscountCode): string {
    return code.origin === 'ADMIN' ? 'Código de descuento' : 'Cupón';
}

export function formatDiscount(discount: DiscountValue): string {
    if (discount.type === 'AMOUNT') return formatCents(discount.amountCents ?? 0);
    return `${String((discount.percentBps ?? 0) / 100)}%`;
}

export function formatCodeUses(code: DiscountCode): string {
    const uses = formatInt(code.uses);
    return code.maxUses === null ? `${uses} · sin tope` : `${uses} de ${formatInt(code.maxUses)}`;
}

export function formatCodeValidity(code: DiscountCode): string {
    const { validFrom, validUntil } = code;
    if (validFrom === null && validUntil === null) return 'Permanente';
    if (validUntil === null) return `Desde ${formatDateMx(validFrom ?? '')}`;
    if (validFrom === null) return `Hasta ${formatDateMx(validUntil)}`;
    return `${formatDateMx(validFrom)} → ${formatDateMx(validUntil)}`;
}

export function formatOrigin(code: DiscountCode): string {
    if (code.origin === 'ADMIN') return 'Admin VL';
    return code.issuedBySaleId ? `Venta ${code.issuedBySaleId}` : 'Venta';
}

export type RedeemedIn = { kind: 'sale'; saleId: string } | { kind: 'text'; text: string };

/** Un solo uso muestra la venta; varios usos, cuántas ventas lo canjearon, nunca una suelta. */
export function redeemedIn(code: DiscountCode): RedeemedIn {
    const singleUse = code.origin === 'SALE_COUPON' || code.maxUses === 1;
    if (singleUse) {
        return code.redeemedBySaleId === null
            ? { kind: 'text', text: NONE }
            : { kind: 'sale', saleId: code.redeemedBySaleId };
    }
    if (code.uses === 0) return { kind: 'text', text: NONE };
    return { kind: 'text', text: code.uses === 1 ? '1 venta' : `${formatInt(code.uses)} ventas` };
}

export function isVoidable(code: DiscountCode): boolean {
    return code.status === 'ACTIVE';
}

/** Tiendas completas para el detalle; los ids que el padrón no resuelve van tal cual. */
export function listCodeStores(code: DiscountCode, storeNames: Map<string, string>): string {
    if (code.couponScope !== null) return COUPON_SCOPE_TEXT[code.couponScope];
    if (code.storeIds === null) return 'Toda la cadena';
    return code.storeIds
        .map((id) => storeNames.get(id) ?? id)
        .sort((a, b) => a.localeCompare(b, 'es-MX'))
        .join(', ');
}
